import React from 'react'

import ReactDOM from 'react-dom/client'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { ChakraProvider } from '@chakra-ui/react'
import { GoogleOAuthProvider } from '@react-oauth/google'
import * as Sentry from '@sentry/react'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { AppVersionProvider } from '@/context/AppVersionContext'
import { AuthProvider } from '@/context/AuthContext'
import { queryClient } from '@/react-query/client'
import { LayoutWithHeader } from '@/views/layouts/LayoutWithHeader/LayoutWithHeader'
import { SignInLayout } from '@/views/layouts/SignInLayout'
import ForgotPassword from '@/views/screens/ForgotPassword/ForgotPassword'
import Home from '@/views/screens/Home'
import Interview from '@/views/screens/Interview'
import PricingPage from '@/views/screens/PricingPage'
import SignIn from '@/views/screens/SignIn'
import SignUp, { ReferralRedirect } from '@/views/screens/SignUp'
import SignUpName from '@/views/screens/SignUpName'
import Team from '@/views/screens/Team'
import VerifyEmail from '@/views/screens/VerifyEmail/VerifyEmail'
import AuthedRoute from '@/views/service-components/AuthedRoute'
import SignInRoute from '@/views/service-components/SignInRoute'

import chakraTheme from './views/chakra-custom-theme/chakra-theme'
import { RecordingOptOut } from './views/screens/RecordingOptOut/RecordingOptOut'
import { UserSettings } from './views/screens/UserSettings'

import './index.css'

Sentry.init({
  enabled: !import.meta.env.DEV,
  dsn: 'https://4a05106f6fd448e1aae2e41073c7352b@o4508753966530560.ingest.us.sentry.io/4508753968824320',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.captureConsoleIntegration({
      levels: ['error', 'warn'],
    }),
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [[400, 599]],
    }),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE || 'production',
})

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_API_KEY)

const router = createBrowserRouter([
  {
    path: '/',
    element: <SignInLayout />,
    children: [
      {
        path: 'sign-in',
        element: <SignInRoute component={<SignIn />} />,
      },
      {
        path: 'sign-up',
        element: <SignInRoute component={<SignUp />} />,
      },
      {
        path: 'sign-up/name',
        element: <AuthedRoute component={<SignUpName />} />,
      },
      {
        path: 'verify-email',
        element: <AuthedRoute component={<VerifyEmail />} />,
      },
      {
        path: 'forgot-password',
        element: <SignInRoute component={<ForgotPassword />} />,
      },
    ],
  },
  {
    path: '/',
    element: <LayoutWithHeader />,
    children: [
      {
        path: 'home',
        element: <AuthedRoute component={<Home />} />,
      },
      {
        path: 'interview/:interviewId',
        element: <AuthedRoute component={<Interview />} />,
      },
      {
        path: 'pricing-page',
        element: <AuthedRoute component={<PricingPage />} />,
      },
      {
        path: 'team/:inviteId?',
        element: <AuthedRoute component={<Team />} />,
      },
      {
        path: 'settings',
        element: <AuthedRoute component={<UserSettings />} />,
      },
      {
        index: true,
        element: <Navigate to="/home" />,
      },
    ],
  },
  {
    path: '/join/:referralCode',
    element: <ReferralRedirect />,
  },
  {
    path: '/recording-optout/:optoutId',
    element: <RecordingOptOut />,
  },
  {
    path: '*',
    element: <Navigate to="/home" />,
  },
])

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId="622008333070-liattjaalqtneplktifu8irdfq19fll9.apps.googleusercontent.com">
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools />
        <AppVersionProvider>
          <ChakraProvider theme={chakraTheme}>
            <AuthProvider>
              <Elements stripe={stripePromise}>
                <RouterProvider router={router} />
              </Elements>
            </AuthProvider>
          </ChakraProvider>
        </AppVersionProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  </React.StrictMode>,
)
